import type { fabric } from 'fabric';
import React, { useCallback } from 'react';
import { registerInspector } from './inspector-registry';
import PlaceholderTransformSection from './placeholder-transform-section';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import PlaceholderIdentitySection from './shared/placeholder-identity-section';
import SelectField from './shared/select-field';
import { useFabricData } from './shared/use-fabric-data';
import type { ObjectInspectorProps } from './types';

const FIT_MODES = [
  { value: 'contain', label: 'Contain' },
  { value: 'cover', label: 'Cover' },
];

const PlaceholderImageInspector: React.FC<ObjectInspectorProps> = ({ object, editor }) => {
  const [fitMode, setFitMode] = useFabricData<string>(object, 'fitMode', editor);
  const [previewMode, setPreviewMode] = useFabricData<boolean>(object, 'previewMode', editor);

  const refit = useCallback(() => {
    editor.placeholders?.refit(object as fabric.Group);
  }, [editor, object]);

  return (
    <>
      <InspectorSection title="Preview">
        <InspectorRow label="Preview mode">
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={Boolean(previewMode)}
              onChange={(e) => {
                setPreviewMode(e.target.checked || undefined);
                refit();
              }}
            />
            <span>show as substituted image</span>
          </CheckboxLabel>
        </InspectorRow>
      </InspectorSection>
      <PlaceholderIdentitySection object={object} editor={editor} />
      <InspectorSection title="Image">
        <InspectorRow label="Fit mode">
          <SelectField
            value={fitMode ?? 'contain'}
            onChange={(v) => {
              setFitMode(v);
              refit();
            }}
            options={FIT_MODES}
          />
        </InspectorRow>
      </InspectorSection>
      <PlaceholderTransformSection object={object} editor={editor} />
    </>
  );
};

export default PlaceholderImageInspector;

registerInspector('placeholder-image', PlaceholderImageInspector);

const CheckboxLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'inline-flex',
      gap: 6,
      alignItems: 'center',
      fontSize: 12,
      color: '#374151',
      cursor: 'pointer',
    }}
  >
    {children}
  </label>
);
